import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Team, ModuleId, DEFAULT_MODULE_ORDER } from '@/types/team';
import { isAuthRequired } from '@/lib/environment';
import { toast } from '@/lib/toast';

// Get current user id for team creation
const getCurrentUserId = async (): Promise<string | null> => {
  // Prefer session (local) over getUser() (network) to be resilient in PWA/offline-ish states
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.user?.id) return session.user.id;

  const { data: { user } } = await supabase.auth.getUser();
  return user?.id || null;
};

const CURRENT_TEAM_KEY = 'team-v-does-current-team'; // Team storage key
const DEFAULT_TEAM_KEY = 'team-v-does-default-team'; // Configured default team key
const TEAMS_CACHE_KEY = 'team-v-does-teams-cache'; // Teams cache key
const TEAMS_DEV_CACHE_KEY = 'team-v-does-teams-cache-dev'; // Dev fallback cache
const LAST_USER_ID_KEY = 'team-v-does-last-user-id'; // Used to prevent cross-user cache flash
const TASKS_CACHE_PREFIX = 'team-v-does-tasks-cache:'; // Must match useTasks
const TEAM_MEMBERS_BLOCKED_KEY = 'team-v-does-team-members-blocked';

type TeamsCachePayload = {
  v: 1;
  userId: string | null;
  teams: any[];
};

const loadTeamsCachePayload = (cacheKey: string): TeamsCachePayload | null => {
  try {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const parsed = JSON.parse(cached);
    if (!parsed || typeof parsed !== 'object') return null;

    // Backwards compatibility: older versions stored just Team[]
    if (Array.isArray(parsed)) {
      return { v: 1, userId: null, teams: parsed };
    }

    if (parsed.v !== 1 || !Array.isArray(parsed.teams)) return null;
    return {
      v: 1,
      userId: typeof parsed.userId === 'string' ? parsed.userId : null,
      teams: parsed.teams,
    };
  } catch {
    return null;
  }
};

const getCachedTeamsUserId = (): string | null => {
  return loadTeamsCachePayload(TEAMS_CACHE_KEY)?.userId ?? null;
};

// Load teams from localStorage cache
const loadTeamsFromCache = (): Team[] => {
  if (isAuthRequired()) {
    try {
      const lastUserId = localStorage.getItem(LAST_USER_ID_KEY);
      if (!lastUserId) return [];
    } catch {
      return [];
    }
  }
  const payload =
    loadTeamsCachePayload(TEAMS_CACHE_KEY) ||
    (!isAuthRequired() ? loadTeamsCachePayload(TEAMS_DEV_CACHE_KEY) : null);
  if (!payload) return [];

  try {
    // Restore Date objects
    return payload.teams.map((t: any) => ({ ...t, createdAt: new Date(t.createdAt) }));
  } catch {
    return [];
  }
};

// Save teams to localStorage cache
const saveTeamsToCache = (teams: Team[], userId: string | null) => {
  try {
    const payload: TeamsCachePayload = {
      v: 1,
      userId,
      teams,
    };
    localStorage.setItem(TEAMS_CACHE_KEY, JSON.stringify(payload));
    if (!isAuthRequired()) {
      localStorage.setItem(TEAMS_DEV_CACHE_KEY, JSON.stringify(payload));
    }
  } catch {
    // ignore
  }
};

const dbRowToTeam = (row: any): Team => {
  // Load localStorage settings for module labels and tag icons (DB columns don't exist)
  const storedModuleLabels = localStorage.getItem(`team-module-labels-${row.id}`);
  const storedTagIcons = localStorage.getItem(`team-tag-icons-${row.id}`);

  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    slogan: row.slogan,
    imageUrl: row.image_url,
    createdAt: new Date(row.created_at),
    createdByUserId: row.created_by_user_id || null,
    weekendStartDay: row.weekend_start_day ?? 5, // Default: Friday
    weekendStartHour: row.weekend_start_hour ?? 17, // Default: 17:00
    weekendEndDay: row.weekend_end_day ?? 0, // Default: Sunday
    weekendEndHour: row.weekend_end_hour ?? 0, // Default: midnight
    morningHour: row.morning_hour ?? 6, // Default: 06:00
    eveningHour: row.evening_hour ?? 18, // Default: 18:00
    showFypModule: row.show_fyp_module ?? true, // Default: true (new column)
    showTasksModule: row.show_tasks_module ?? true, // Default: true
    showCompletedModule: row.show_completed_module ?? true, // Default: true
    showGroceryModule: row.show_grocery_module ?? true, // Default: true
    showStockModule: row.show_stock_module ?? true, // Default: true
    showGoalsModule: row.show_goals_module ?? false, // Default: false
    moduleOrder: (() => {
      const dbOrder = (row.module_order as ModuleId[]) || DEFAULT_MODULE_ORDER;
      // Ensure 'fyp' is present. If missing, prepend it (as per requirement "first tab on the left")
      const orderWithFyp = dbOrder.includes('fyp') ? dbOrder : ['fyp', ...dbOrder];
      // Ensure all other default modules are present (appended if missing)
      return [...new Set([...orderWithFyp, ...DEFAULT_MODULE_ORDER])] as ModuleId[];
    })(),
    moduleLabels: storedModuleLabels ? JSON.parse(storedModuleLabels) : {},
    tagIcons: storedTagIcons ? JSON.parse(storedTagIcons) : {},
    language: (row.language as 'nl' | 'en') || 'nl',
    themePrimaryColor: row.theme_primary_color || '#57876b',
    actionButtonBgColor: row.action_button_bg_color || '#344e41',
    actionButtonIconColor: row.action_button_icon_color || '#ffffff',
    isPublic: row.is_public ?? false,
    inviteCode: row.invite_code ?? null,
    inviteCodeEnabled: row.invite_code_enabled ?? false,
  };
};

export function useTeams() {
  const initialTeamMembersBlocked = (() => {
    try {
      return localStorage.getItem(TEAM_MEMBERS_BLOCKED_KEY) === '1';
    } catch {
      return false;
    }
  })();
  // Initialize from localStorage cache so UI is stable on refresh
  const [teams, setTeamsState] = useState<Team[]>(() => loadTeamsFromCache());
  const [currentTeamId, setCurrentTeamIdState] = useState<string | null>(() => {
    return localStorage.getItem(DEFAULT_TEAM_KEY) || localStorage.getItem(CURRENT_TEAM_KEY);
  });
  // Only show loading if we have no cached data
  const [loading, setLoading] = useState(() => loadTeamsFromCache().length === 0);

  const [userId, setUserId] = useState<string | null>(null);
  const [teamMemberships, setTeamMemberships] = useState<{ teamId: string; role: 'admin' | 'member' }[]>([]);
  const teamMembersBlockedRef = useRef(initialTeamMembersBlocked);
  const teamMembersErrorLoggedRef = useRef(false);
  const isFetchingRef = useRef(false);

  // Wrapper that also persists to cache
  const setTeams = useCallback(
    (updater: Team[] | ((prev: Team[]) => Team[])) => {
      setTeamsState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        saveTeamsToCache(next, userId);
        return next;
      });
    },
    [userId]
  );

  // Track auth state
  useEffect(() => {
    const loadLastUserId = (): string | null => {
      try {
        return localStorage.getItem(LAST_USER_ID_KEY);
      } catch {
        return null;
      }
    };

    const persistLastUserId = (id: string | null) => {
      try {
        if (id) localStorage.setItem(LAST_USER_ID_KEY, id);
        else localStorage.removeItem(LAST_USER_ID_KEY);
      } catch {
        // ignore
      }
    };

    // Seed from last-known user OR from cache payload to prevent a null→user flash on refresh.
    let previousUserId: string | null = loadLastUserId() ?? getCachedTeamsUserId();

    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const nextUserId = session?.user?.id || null;
      previousUserId = nextUserId;
      persistLastUserId(nextUserId);
      setUserId(nextUserId);
    };
    getUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      const nextUserId = session?.user?.id || null;

      const effectivePrevUserId = previousUserId ?? getCachedTeamsUserId() ?? loadLastUserId();

      // If user changed (login/logout/switch), clear any persisted team selection AND cache
      // to prevent stale team data leaking between sessions.
      const shouldClearCache =
        effectivePrevUserId !== nextUserId &&
        (isAuthRequired() || (effectivePrevUserId && nextUserId));

      if (shouldClearCache) {
        try {
          localStorage.removeItem(CURRENT_TEAM_KEY);
          localStorage.removeItem(DEFAULT_TEAM_KEY);
          localStorage.removeItem(TEAMS_CACHE_KEY);

          // Also clear per-team task caches to prevent cross-user flashes.
          for (let i = localStorage.length - 1; i >= 0; i--) {
            const k = localStorage.key(i);
            if (k && k.startsWith(TASKS_CACHE_PREFIX)) {
              localStorage.removeItem(k);
            }
          }
        } catch {
          // ignore
        }
        setCurrentTeamIdState(null);
        setTeamsState([]); // Clear in-memory too
      }

      previousUserId = nextUserId;
      persistLastUserId(nextUserId);
      setUserId(nextUserId);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Clear cache on auth change, but keep loading state accurate
  const fetchTeams = useCallback(async (isBackgroundRefresh = false) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    // Helper to ensure loading is cleared
    const finish = () => {
      if (!isBackgroundRefresh) setLoading(false);
    };

    try {
      if (!isBackgroundRefresh && teams.length === 0) {
        setLoading(true);
      }

      // Inner try for the actual work
      try {
        // Get current user
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        const user = session?.user ?? null;
        if (sessionError) {
          console.warn('[useTeams] getSession error:', sessionError);
        }

        const DEVICE_AUTHORIZED_TEAM_KEY = 'team_v_authorized_team';
        const authorizedTeamId = (() => {
          try {
            return localStorage.getItem(DEVICE_AUTHORIZED_TEAM_KEY);
          } catch {
            return null;
          }
        })();

        if (!user) {
          setTeamMemberships([]);
          if (!isAuthRequired()) {
            const { data, error } = await supabase.from('teams').select('*').order('name');
            if (error) {
              console.error('Error fetching teams (dev mode):', error);
              return;
            }
            setTeams(data?.map(dbRowToTeam) || []);
            return;
          }

          if (authorizedTeamId) {
            let teamData: any = null;
            const directResult = await supabase
              .from('teams')
              .select('*')
              .eq('id', authorizedTeamId)
              .maybeSingle();

            if (directResult.error) {
              console.error('Error fetching authorized team natively:', directResult.error);
            } else {
              teamData = directResult.data;
            }

            // Fallback to RPC if anon RLS blocked the read
            if (!teamData) {
              const rpcResult = await supabase.rpc('get_team_by_id', { _team_id: authorizedTeamId });
              if (rpcResult.error || !rpcResult.data) {
                console.error('Error fetching authorized team via RPC:', rpcResult.error);
                setTeams([]);
                setCurrentTeamIdState(null);
                return;
              }
              teamData = rpcResult.data;
            }

            setTeams([dbRowToTeam(teamData)]);
            setCurrentTeamIdState(authorizedTeamId);
            return;
          }

          setTeams([]);
          setCurrentTeamIdState(null);
          return;
        }

        const userEmail = (user.email || '').trim().toLowerCase();
        if (userEmail) {
          const { data: unlinkEmails } = await supabase
            .from('family_member_emails')
            .select('id')
            .is('user_id', null)
            .ilike('email', userEmail);

          if (unlinkEmails && unlinkEmails.length > 0) {
            await supabase
              .from('family_member_emails')
              .update({ user_id: user.id })
              .in('id', unlinkEmails.map(e => e.id));
          }

          await supabase
            .from('family_members')
            .update({ user_id: user.id })
            .is('user_id', null)
            .ilike('email', userEmail);
        }

        const memberPromise = teamMembersBlockedRef.current
          ? Promise.resolve({ data: [], error: null })
          : supabase.from('team_members').select('team_id, role').eq('user_id', user.id);

        const [
          { data: memberData, error: memberError },
          { data: ownedData, error: ownedError },
          { data: familyLinksData, error: familyError },
          { data: emailLinksData, error: emailError },
          { data: profileData, error: profileError },
        ] = await Promise.all([
          memberPromise,
          supabase.from('teams').select('*').eq('created_by_user_id', user.id),
          supabase.from('family_members').select('team_id').eq('user_id', user.id).not('team_id', 'is', null),
          supabase.from('family_member_emails').select('team_id').eq('user_id', user.id),
          supabase.from('profiles').select('default_team_id').eq('user_id', user.id).maybeSingle(),
        ]);

        const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
        const hasNetworkError = (err: any) => err?.message === 'Failed to fetch' || err?.message?.includes('Network Error');

        if (
          isOffline ||
          hasNetworkError(memberError) ||
          hasNetworkError(ownedError) ||
          hasNetworkError(familyError) ||
          hasNetworkError(emailError) ||
          hasNetworkError(profileError)
        ) {
          console.warn('[useTeams] Offline or network error detected, keeping cached teams');
          return;
        }


        if (memberError?.code === '42P17') {
          teamMembersBlockedRef.current = true;
          try { localStorage.setItem(TEAM_MEMBERS_BLOCKED_KEY, '1'); } catch { }
        }

        const ownedTeamIds = (ownedData || []).map(t => t.id);
        const familyTeamIds = (familyLinksData?.map(r => r.team_id).filter(Boolean) as string[]) || [];
        const emailTeamIds = (emailLinksData?.map(r => r.team_id).filter(Boolean) as string[]) || [];
        const allFamilyTeamIds = Array.from(new Set([...familyTeamIds, ...emailTeamIds]));
        const memberTeamIds = (memberData?.map(m => m.team_id).filter(Boolean) as string[]) || [];

        const allTeamIds = Array.from(new Set([...memberTeamIds, ...ownedTeamIds, ...allFamilyTeamIds]));

        if (allTeamIds.length === 0) {
          setTeams([]);
          setCurrentTeamIdState(null);
          return;
        }

        const { data: teamsData, error: teamsError } = await supabase
          .from('teams')
          .select('*')
          .in('id', allTeamIds)
          .order('name');

        if (teamsError) {
          console.error('Error fetching teams:', teamsError);
          if (isOffline || hasNetworkError(teamsError)) {
            console.warn('[useTeams] Offline or network error fetching teams, keeping cached teams');
          } else {
            setTeams([]);
          }
          return;
        }

        if (!teamMembersBlockedRef.current && memberData) {
          setTeamMemberships(memberData.map(m => ({ teamId: m.team_id, role: m.role as 'admin' | 'member' })));
        }

        // Restore default team from profile if missing locally
        if (profileData?.default_team_id && allTeamIds.includes(profileData.default_team_id)) {
          try {
            if (!localStorage.getItem(DEFAULT_TEAM_KEY)) {
              localStorage.setItem(DEFAULT_TEAM_KEY, profileData.default_team_id);
            }
          } catch { }
        }

        setTeams(teamsData?.map(dbRowToTeam) || []);
      } catch (err) {
        console.error('[useTeams] fetchTeams inner error:', err);
      } finally {
        finish();
      }
    } finally {
      isFetchingRef.current = false;
    }
  }, [setTeams, teams.length]);
  // Removed teams.length to prevent loop

  useEffect(() => {
    // Initial fetch - show loading only if no cache
    fetchTeams(false);

    // Refetch when auth state changes (background refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      fetchTeams(true); // background refresh - keep existing data visible
    });

    const channel = supabase
      .channel('teams-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'teams' },
        () => {
          fetchTeams(true); // background refresh
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'team_members' },
        () => {
          fetchTeams(true); // background refresh
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
      supabase.removeChannel(channel);
    };
  }, [fetchTeams]);

  const setCurrentTeamId = useCallback((id: string | null) => {
    if (id) {
      localStorage.setItem(CURRENT_TEAM_KEY, id);
    } else {
      localStorage.removeItem(CURRENT_TEAM_KEY);
    }
    setCurrentTeamIdState(id);
  }, []);

  // Auto-select first available team when no team is selected
  useEffect(() => {
    if (!loading && teams.length === 0 && currentTeamId) {
      setCurrentTeamId(null);
      return;
    }

    if (!loading && teams.length > 0 && !currentTeamId) {
      const defaultTeamId = localStorage.getItem(DEFAULT_TEAM_KEY);
      if (defaultTeamId && teams.find(t => t.id === defaultTeamId)) {
        setCurrentTeamId(defaultTeamId);
      } else {
        // Select first available team
        setCurrentTeamId(teams[0].id);
      }
    }
    // Clear current team if it's no longer accessible
    if (!loading && currentTeamId && teams.length > 0 && !teams.find(t => t.id === currentTeamId)) {
      const defaultTeamId = localStorage.getItem(DEFAULT_TEAM_KEY);
      if (defaultTeamId && teams.find(t => t.id === defaultTeamId)) {
        setCurrentTeamId(defaultTeamId);
      } else {
        setCurrentTeamId(teams[0].id);
      }
    }
  }, [loading, teams, currentTeamId, setCurrentTeamId]);

  // Team activity heartbeat: update last_active_at every 5 minutes
  useEffect(() => {
    if (!currentTeamId || !userId) return;

    const sendHeartbeat = async () => {
      try {
        await supabase
          .from('team_members')
          .update({ last_active_at: new Date().toISOString() })
          .eq('team_id', currentTeamId)
          .eq('user_id', userId);
      } catch (err) {
        // Silently ignore heartbeat failures
        console.debug('[useTeams] Heartbeat failed:', err);
      }
    };

    // Fire immediately on team switch
    sendHeartbeat();

    // Then every 5 minutes
    const interval = setInterval(sendHeartbeat, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [currentTeamId, userId]);

  const addTeam = useCallback(async (name: string): Promise<string | null> => {
    const trimmedName = name.trim();
    if (!trimmedName) return null;

    // Get current user for creator tracking
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    const currentUserId = user?.id || null;

    // RLS requires authenticated user - prevent creation when not logged in
    if (!currentUserId) {
      toast.error('Je moet ingelogd zijn om een team aan te maken', { channel: 'teams' });
      return null;
    }

    // Create default slogan for new team
    const defaultSlogan = `${trimmedName} Does`;

    const { data, error } = await supabase
      .from('teams')
      .insert({
        name: trimmedName,
        slogan: defaultSlogan,
        created_by_user_id: currentUserId,
      })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        toast.error('Deze teamnaam bestaat al', { channel: 'teams' });
      } else {
        console.error('Error adding team:', error);
        toast.error('Fout bij toevoegen van team', { channel: 'teams' });
      }
      return null;
    }

    // Automatically add user as team admin
    const { error: memberError } = await supabase
      .from('team_members')
      .insert({
        team_id: data.id,
        user_id: currentUserId,
        role: 'admin',
      });

    if (memberError && memberError.code !== '23505') {
      console.error('Error adding team creator as admin:', memberError);
    }

    // Create a family_members entry for the team creator so they appear in the team member list
    // Use display name from user metadata, or extract from email
    const displayName = user?.user_metadata?.full_name
      || user?.user_metadata?.name
      || user?.email?.split('@')[0]
      || 'Eigenaar';

    const { data: familyMemberData, error: familyError } = await supabase
      .from('family_members')
      .insert({
        name: displayName,
        team_id: data.id,
        user_id: currentUserId,
        email: user?.email || null,
      })
      .select()
      .single();

    if (familyError) {
      console.error('Error creating family member for team creator:', familyError);
    } else if (familyMemberData && user?.email) {
      // Also add the email to family_member_emails for the new multi-email system
      const { error: emailError } = await supabase
        .from('family_member_emails')
        .insert({
          family_member_id: familyMemberData.id,
          team_id: data.id,
          email: user.email.toLowerCase(),
          user_id: currentUserId,
          is_primary: true,
        });

      if (emailError && emailError.code !== '23505') {
        console.error('Error adding creator email to family_member_emails:', emailError);
      }
    }

    // Optimistically add to local state immediately
    const newTeam = dbRowToTeam(data);
    setTeams(prev => [...prev, newTeam].sort((a, b) => a.name.localeCompare(b.name)));

    toast.success(`Team "${trimmedName}" aangemaakt!`, { channel: 'teams' });
    return data.id;
  }, []);

  const updateTeamSlogan = useCallback(async (teamId: string, slogan: string): Promise<void> => {
    const { error } = await supabase
      .from('teams')
      .update({ slogan })
      .eq('id', teamId);

    if (error) {
      console.error('Error updating team slogan:', error);
      toast.error('Fout bij bijwerken van slogan', { channel: 'teams' });
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, slogan } : t
    ));

    toast.success('Slogan bijgewerkt!', { channel: 'teams' });
  }, []);

  const updateTeamName = useCallback(async (teamId: string, name: string): Promise<void> => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    const { error } = await supabase
      .from('teams')
      .update({ name: trimmedName })
      .eq('id', teamId);

    if (error) {
      if (error.code === '23505') {
        toast.error('Deze teamnaam bestaat al', { channel: 'teams' });
      } else {
        console.error('Error updating team name:', error);
        toast.error('Fout bij bijwerken van teamnaam', { channel: 'teams' });
      }
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, name: trimmedName } : t
    ).sort((a, b) => a.name.localeCompare(b.name)));

    toast.success('Teamnaam bijgewerkt!', { channel: 'teams' });
  }, []);

  const updateTeamImage = useCallback(async (teamId: string, imageUrl: string | null): Promise<void> => {
    const { error } = await supabase
      .from('teams')
      .update({ image_url: imageUrl })
      .eq('id', teamId);

    if (error) {
      console.error('Error updating team image:', error);
      toast.error('Fout bij bijwerken van teamfoto');
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, imageUrl } : t
    ));

    toast.success('Teamfoto bijgewerkt!');
  }, []);

  const updateWeekendWindow = useCallback(async (
    teamId: string,
    settings: {
      weekendStartDay?: number;
      weekendStartHour?: number;
      weekendEndDay?: number;
      weekendEndHour?: number;
    }
  ): Promise<void> => {
    const updates: Record<string, any> = {};
    if (settings.weekendStartDay !== undefined) updates.weekend_start_day = settings.weekendStartDay;
    if (settings.weekendStartHour !== undefined) updates.weekend_start_hour = settings.weekendStartHour;
    if (settings.weekendEndDay !== undefined) updates.weekend_end_day = settings.weekendEndDay;
    if (settings.weekendEndHour !== undefined) updates.weekend_end_hour = settings.weekendEndHour;

    const { error } = await supabase
      .from('teams')
      .update(updates)
      .eq('id', teamId);

    if (error) {
      console.error('Error updating weekend window:', error);
      toast.error('Fout bij bijwerken van weekend-instellingen');
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, ...settings } : t
    ));

    toast.success('Weekend-instellingen bijgewerkt!');
  }, []);

  const updateDayPeriodHours = useCallback(async (
    teamId: string,
    settings: {
      morningHour?: number;
      eveningHour?: number;
    }
  ): Promise<void> => {
    const updates: Record<string, any> = {};
    if (settings.morningHour !== undefined) updates.morning_hour = settings.morningHour;
    if (settings.eveningHour !== undefined) updates.evening_hour = settings.eveningHour;

    const { error } = await supabase
      .from('teams')
      .update(updates)
      .eq('id', teamId);

    if (error) {
      console.error('Error updating day period hours:', error);
      toast.error('Fout bij bijwerken van dagdeel-instellingen');
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, ...settings } : t
    ));

    toast.success('Dagdeel-instellingen bijgewerkt!');
  }, []);

  const updateTeamSlug = useCallback(async (teamId: string, slug: string): Promise<boolean> => {
    const trimmedSlug = slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
    if (!trimmedSlug) {
      toast.error('URL-naam mag niet leeg zijn');
      return false;
    }

    // Check if slug is already taken by another team
    const { data: existing } = await supabase
      .from('teams')
      .select('id')
      .eq('slug', trimmedSlug)
      .neq('id', teamId)
      .single();

    if (existing) {
      toast.error('Deze URL-naam is al in gebruik door een ander team');
      return false;
    }

    const { error } = await supabase
      .from('teams')
      .update({ slug: trimmedSlug })
      .eq('id', teamId);

    if (error) {
      if (error.code === '23505') {
        toast.error('Deze URL-naam is al in gebruik');
      } else {
        console.error('Error updating team slug:', error);
        toast.error('Fout bij bijwerken van URL-naam');
      }
      return false;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, slug: trimmedSlug } : t
    ));

    toast.success('URL-naam bijgewerkt!');
    return true;
  }, []);

  const updateModuleVisibility = useCallback(async (
    teamId: string,
    settings: {
      showFypModule?: boolean;
      showTasksModule?: boolean;
      showCompletedModule?: boolean;
      showGroceryModule?: boolean;
      showStockModule?: boolean;
      showGoalsModule?: boolean;
    }
  ): Promise<void> => {
    const updates: Record<string, any> = {};
    if (settings.showFypModule !== undefined) updates.show_fyp_module = settings.showFypModule;
    if (settings.showTasksModule !== undefined) updates.show_tasks_module = settings.showTasksModule;
    if (settings.showCompletedModule !== undefined) updates.show_completed_module = settings.showCompletedModule;
    if (settings.showGroceryModule !== undefined) updates.show_grocery_module = settings.showGroceryModule;
    if (settings.showStockModule !== undefined) updates.show_stock_module = settings.showStockModule;
    if (settings.showGoalsModule !== undefined) updates.show_goals_module = settings.showGoalsModule;

    const { error } = await supabase
      .from('teams')
      .update(updates)
      .eq('id', teamId);

    if (error) {
      console.error('Error updating module visibility:', error);
      toast.error('Fout bij bijwerken van module-instellingen', { channel: 'teams' });
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, ...settings } : t
    ));

    toast.success('Module-instellingen bijgewerkt!', { channel: 'teams' });
  }, []);

  const updateModuleOrder = useCallback(async (
    teamId: string,
    moduleOrder: ModuleId[]
  ): Promise<void> => {
    const { error } = await supabase
      .from('teams')
      .update({ module_order: moduleOrder })
      .eq('id', teamId);

    if (error) {
      console.error('Error updating module order:', error);
      toast.error('Fout bij bijwerken van module-volgorde', { channel: 'teams' });
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, moduleOrder } : t
    ));

    toast.success('Module-volgorde bijgewerkt!', { channel: 'teams' });
  }, []);

  const updateTeamModuleLabels = useCallback(async (
    teamId: string,
    moduleLabels: Team['moduleLabels']
  ): Promise<void> => {
    // Store in localStorage since DB column doesn't exist yet
    localStorage.setItem(`team-module-labels-${teamId}`, JSON.stringify(moduleLabels || {}));

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, moduleLabels: moduleLabels || {} } : t
    ));

    toast.success('Module labels bijgewerkt!', { channel: 'teams' });
  }, []);

  const updateTeamTagIcons = useCallback(async (
    teamId: string,
    tagIcons: Team['tagIcons']
  ): Promise<void> => {
    // Store in localStorage since DB column doesn't exist yet
    localStorage.setItem(`team-tag-icons-${teamId}`, JSON.stringify(tagIcons || {}));

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, tagIcons: tagIcons || {} } : t
    ));

    toast.success('Tag iconen bijgewerkt!', { channel: 'teams' });
  }, []);

  const updateTeamVisibility = useCallback(async (teamId: string, isPublic: boolean): Promise<void> => {
    const { error } = await supabase
      .from('teams')
      .update({ is_public: isPublic })
      .eq('id', teamId);

    if (error) {
      console.error('Error updating team visibility:', error);
      toast.error('Fout bij bijwerken van team zichtbaarheid', { channel: 'teams' });
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, isPublic } : t
    ));

    toast.success(isPublic ? 'Team is nu openbaar!' : 'Team is nu privé!', { channel: 'teams' });
  }, []);

  const updateInviteCode = useCallback(async (teamId: string, code: string): Promise<boolean> => {
    const trimmedCode = code.trim().toUpperCase();
    if (!trimmedCode) {
      toast.error('Code mag niet leeg zijn', { channel: 'teams' });
      return false;
    }

    const { error } = await supabase
      .from('teams')
      .update({ invite_code: trimmedCode } as any)
      .eq('id', teamId);

    if (error) {
      if (error.code === '23505') {
        toast.error('Deze code is al in gebruik', { channel: 'teams' });
      } else {
        console.error('Error updating invite code:', error);
        toast.error('Fout bij bijwerken van uitnodigingscode', { channel: 'teams' });
      }
      return false;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, inviteCode: trimmedCode } : t
    ));
    toast.success('Uitnodigingscode bijgewerkt!', { channel: 'teams' });
    return true;
  }, [setTeams]);

  const updateInviteCodeEnabled = useCallback(async (teamId: string, enabled: boolean): Promise<void> => {
    const { error } = await supabase
      .from('teams')
      .update({ invite_code_enabled: enabled } as any)
      .eq('id', teamId);

    if (error) {
      console.error('Error updating invite code enabled:', error);
      toast.error('Fout bij bijwerken van uitnodigingscode', { channel: 'teams' });
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, inviteCodeEnabled: enabled } : t
    ));
    toast.success(enabled ? 'Uitnodigingscode geactiveerd!' : 'Uitnodigingscode gedeactiveerd!', { channel: 'teams' });
  }, [setTeams]);

  const joinTeamByCode = useCallback(async (code: string): Promise<{ success: boolean; teamId?: string; teamName?: string; alreadyMember?: boolean }> => {
    const { data, error } = await supabase.rpc('join_team_by_invite_code', { _code: code.trim() });

    if (error) {
      console.error('Error joining team by code:', error);
      toast.error('Fout bij het toetreden tot team', { channel: 'teams' });
      return { success: false };
    }

    const result = data as any;
    if (!result.success) {
      toast.error(result.error === 'Invalid or disabled invite code' ? 'Ongeldige of uitgeschakelde code' : result.error, { channel: 'teams' });
      return { success: false };
    }

    if (result.already_member) {
      toast.info(`Je bent al lid van ${result.team_name}`, { channel: 'teams' });
    } else {
      toast.success(`Welkom bij ${result.team_name}!`, { channel: 'teams' });
    }

    // Refresh teams
    await fetchTeams(true);
    return { success: true, teamId: result.team_id, teamName: result.team_name, alreadyMember: result.already_member };
  }, [fetchTeams]);

  const updateTeamLanguage = useCallback(async (

    teamId: string,
    language: 'nl' | 'en'
  ): Promise<void> => {
    const { error } = await supabase
      .from('teams')
      .update({ language })
      .eq('id', teamId);

    if (error) {
      console.error('Error updating team language:', error);
      toast.error('Fout bij bijwerken van taalinstellingen', { channel: 'teams' });
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, language } : t
    ));

    toast.success('Taalinstellingen bijgewerkt!', { channel: 'teams' });
  }, []);

  const updateTeamTheme = useCallback(async (
    teamId: string,
    settings: {
      themePrimaryColor?: string;
      actionButtonBgColor?: string;
      actionButtonIconColor?: string;
    }
  ): Promise<void> => {
    const updates: Record<string, any> = {};
    if (settings.themePrimaryColor !== undefined) updates.theme_primary_color = settings.themePrimaryColor;
    if (settings.actionButtonBgColor !== undefined) updates.action_button_bg_color = settings.actionButtonBgColor;
    if (settings.actionButtonIconColor !== undefined) updates.action_button_icon_color = settings.actionButtonIconColor;

    const { error } = await supabase
      .from('teams')
      .update(updates)
      .eq('id', teamId);

    if (error) {
      console.error('Error updating team theme:', error);
      toast.error('Fout bij bijwerken van thema-instellingen', { channel: 'teams' });
      return;
    }

    setTeams(prev => prev.map(t =>
      t.id === teamId ? { ...t, ...settings } : t
    ));
  }, [setTeams]);

  const currentTeam = teams.find(t => t.id === currentTeamId) || null;
  const isTeamMember = useCallback((teamId: string) => {
    return teamMemberships.some(tm => tm.teamId === teamId);
  }, [teamMemberships]);

  const isTeamAdmin = useCallback((teamId: string) => {
    if (teamMemberships.some(tm => tm.teamId === teamId && tm.role === 'admin')) {
      return true;
    }
    const team = teams.find(t => t.id === teamId);
    return !!(team && userId && team.createdByUserId === userId);
  }, [teamMemberships, teams, userId]);

  const searchPublicTeams = useCallback(async (query: string): Promise<Team[]> => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return [];

    const { data, error } = await supabase
      .from('teams')
      .select('*')
      .eq('is_public', true)
      .ilike('name', `%${trimmedQuery}%`)
      .limit(10);

    if (error) {
      console.error('Error searching public teams:', error);
      toast.error('Fout bij zoeken naar teams', { channel: 'teams' });
      return [];
    }

    return (data || []).map(dbRowToTeam);
  }, []);

  const requestTeamAccess = useCallback(async (teamId: string): Promise<boolean> => {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) {
      toast.error('Je moet ingelogd zijn om toegang tot een team te vragen', { channel: 'teams' });
      return false;
    }

    // Try to find the user's member profile to attach to the request if they have one
    const { data: profile } = await supabase
      .from('profiles')
      .select('member_id')
      .eq('user_id', user.id)
      .maybeSingle();

    const { error } = await supabase
      .from('approval_requests')
      .insert({
        team_id: teamId,
        requester_user_id: user.id,
        request_type: 'team_join',
        request_data: { member_id: profile?.member_id || null },
        status: 'pending'
      });

    if (error) {
      if (error.code === '23505') { // Unique violation
        toast.error('Je hebt al toegang aangevraagd voor dit team', { channel: 'teams' });
      } else {
        console.error('Error requesting team access:', error);
        toast.error('Fout bij aanvragen van toegang', { channel: 'teams' });
      }
      return false;
    }

    toast.success('Toegang aangevraagd! De beheerder van het team moet je verzoek nog goedkeuren.', { channel: 'teams' });
    return true;
  }, []);

  return {
    teams,
    teamMemberships,
    currentTeam,
    currentTeamId,
    setCurrentTeamId,
    addTeam,
    updateTeamSlogan,
    updateTeamName,
    updateTeamImage,
    updateTeamSlug,
    updateWeekendWindow,
    updateDayPeriodHours,
    updateModuleVisibility,
    updateModuleOrder,
    updateTeamModuleLabels,
    updateTeamTagIcons,
    updateTeamLanguage,
    updateTeamVisibility,
    searchPublicTeams,
    requestTeamAccess,
    updateTeamTheme,
    updateInviteCode,
    updateInviteCodeEnabled,
    joinTeamByCode,
    isTeamAdmin,
    isTeamMember,
    loading,
    userId,
  };
}
